import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import Sidebar from "./Includes/Sidebar";
import TopBar from "./Includes/TopBar";
import { logoutUser } from "./dispatcher-utils/dispatcher-auth";

const REDIRECT_WAIT_SECONDS = 1.5;

export default function DispatcherDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  // ─── 1. SYNCHRONOUS SECURITY CHECK ───────────────────────────────────────
  const getLocalUser = () => {
    try { return JSON.parse(localStorage.getItem("user")); }
    catch { return null; }
  };
  
  const localUser = getLocalUser();
  const token = localStorage.getItem("token") || localStorage.getItem("user_token") || localStorage.getItem("access_token");

  // Check if session is missing OR if the user is not a dispatcher/company
  const isAuthInvalid = !token || !localUser || (localUser.role !== "dispatcher" && localUser.role !== "company");
  // ───────────────────────────────────────────────────────────────────────

  // ─── 2. STATE HYDRATION & HOOKS ──────────────────────────────────────────
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userData, setUserData] = useState(localUser);

  const [isRedirecting, setIsRedirecting] = useState(isAuthInvalid);
  const [secondsLeft, setSecondsLeft] = useState(REDIRECT_WAIT_SECONDS);
  const startRef = useRef(null);

  const goToLogin = () => navigate("/dispatcher-login", { replace: true });
  const handleLogout = () => logoutUser(navigate);

  // Effect: Clear bad session data if role is incorrect
  useEffect(() => {
    if (isAuthInvalid && localUser && (localUser.role !== "dispatcher" && localUser.role !== "company")) {
      localStorage.removeItem("token");
      localStorage.removeItem("user_token");
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
    }
  }, [isAuthInvalid, localUser]);

  // Effect: Fetch Fresh User Data
  useEffect(() => {
    const fetchProfileData = async () => {
      if (isAuthInvalid) return;
      try {
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
        const userRes = await fetch(`${apiUrl}/api/me/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (userRes.ok) {
          const userJson = await userRes.json();
          setUserData(userJson);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchProfileData();
  }, [isAuthInvalid, token]);

  // Effect: Drives the countdown + progress bar
  useEffect(() => {
    if (!isRedirecting) return;
    startRef.current = Date.now();
    const tick = setInterval(() => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      const remaining = Math.max(0, REDIRECT_WAIT_SECONDS - elapsed);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(tick);
        goToLogin();
      }
    }, 50);
    return () => clearInterval(tick);
  }, [isRedirecting]);

  // ─── 3. EARLY RETURN FOR UNAUTHORIZED USERS ──────────────────────────────
  if (isRedirecting || isAuthInvalid) {
    const progressPct = ((REDIRECT_WAIT_SECONDS - secondsLeft) / REDIRECT_WAIT_SECONDS) * 100;
    return (
      <div className="flex h-screen w-full items-center justify-center bg-gray-50 px-4" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
        <div className="max-w-md w-full bg-white rounded-3xl p-8 text-center shadow-xl border border-gray-100">
          <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 animate-pulse" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Secure session required</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">Redirecting to dispatcher login...</p>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
            <div className="h-full bg-blue-600 rounded-full" style={{ width: `${progressPct}%`, transition: "width 0.05s linear" }} />
          </div>
        </div>
      </div>
    );
  }

  // ─── 4. MAIN AUTHORIZED LAYOUT ───────────────────────────────────────────
  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden" style={{ fontFamily: "Inter,system-ui,sans-serif" }}>
      <Sidebar 
        sidebarOpen={sidebarOpen} 
        setSidebarOpen={setSidebarOpen} 
        handleLogout={handleLogout} 
      />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <TopBar setSidebarOpen={setSidebarOpen} user={userData} />

        {/* The Outlet maps to your actual pages like DispatcherOverview */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <Outlet context={{ user: userData }} />
        </div>
      </div>
    </div>
  );
}